// Sales report — store and per-server metrics (sales-report-store-and-server spec,
// Component 3). Pure functions only: no React, no network, no module state.
//
// Mirrors admin-mobile-app/src/utils/salesMetrics.js. Keep the two byte-identical apart from this one header line, following the existing money.js / menuName.js / addableOrder.js convention. Both surfaces report the same revenue or they eventually disagree about it.
//
// WHY A SEPARATE MODULE rather than more useMemo blocks in AdminOnlineSalesReport.js:
// this is the arithmetic behind a staff perks scheme. A grouping error here does not
// look like a bug, it looks like a number, and someone's bonus is decided from it. So
// it lives somewhere unit-testable without mounting a React tree, following the
// utils/addableOrder.js precedent.
//
// TWO CONVENTIONS INHERITED FROM THE PAGE, deliberately:
//   * Revenue coercion matches the page's `sumCents` exactly -- `amount_total_cents`
//     falling back to `amount_total`, clamped to a non-negative integer. If these ever
//     disagreed the new channel split would not reconcile against the existing
//     "Total sales" KPI, which is the first thing anyone would check.
//   * Item aggregation keys on `ProductID` falling back to name, as the page's
//     `topItems` memo does, so store-only top items rank the same things the same way.
//
// ONE CONVENTION DELIBERATELY NOT INHERITED: the page buckets its trend in a hardcoded
// America/New_York timezone. Hour-of-day and weekday distributions here use the
// caller's timezone (browser local by default, overridable), because "which hour is
// busiest" read in Eastern time is meaningless for an India tenant. The existing trend
// chart is left exactly as it is.
//
// CANCELLED ORDERS: excluded from every revenue figure this module produces
// (Property 8) and reported separately. Note the pre-existing "Total sales" KPI on the
// page does NOT exclude them -- that is untouched pre-existing behaviour, so when a
// range contains cancelled orders the new store figures can legitimately read lower.

export const STORE = 'STORE';
export const ONLINE = 'ONLINE';
export const MARKETPLACE = 'MARKETPLACE';

/** Documented floor for Active_Hours (R5.7), in hours. */
export const MIN_ACTIVE_HOURS = 0.5;

/** Grouping key used for store orders that carry no serverId. */
export const UNASSIGNED_SERVER_KEY = '';

const MARKETPLACE_TOKENS = [
  'otter', 'ubereats', 'uber_eats', 'uber eats', 'doordash', 'door_dash',
  'grubhub', 'swiggy', 'zomato', 'postmates', 'skipthedishes',
];

const MARKETPLACE_ID_KEYS = [
  'otter_order_id', 'ubereats_order_id', 'doordash_order_id', 'marketplaceOrderId',
];

const SETTLED_PAYMENT_STATES = ['paid', 'refunded'];
const CANCELLED_STATES = ['cancelled', 'canceled'];

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const ADD_TYPE = 'ADD_ITEMS';
const REDUCE_TYPES = ['ADJUST_ITEM', 'REMOVE_ITEM'];

function arr(v) {
  return Array.isArray(v) ? v : [];
}

function lower(v) {
  return String(v == null ? '' : v).trim().toLowerCase();
}

function upper(v) {
  return String(v == null ? '' : v).trim().toUpperCase();
}

function intOrZero(v) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
}

/**
 * Safe division for anything shown on the page. Returns null -- an explicit
 * "unavailable" the UI renders as a dash -- rather than NaN or Infinity (Property 7).
 * An empty date range and a server with one order are both entirely ordinary.
 */
function ratio(numerator, denominator) {
  const n = Number(numerator);
  const d = Number(denominator);
  if (!Number.isFinite(n) || !Number.isFinite(d) || d === 0) return null;
  const out = n / d;
  return Number.isFinite(out) ? out : null;
}

/** Revenue in cents for one order. Matches the page's `sumCents` coercion. */
export function orderRevenueCents(order) {
  const cents = order?.amount_total_cents ?? order?.amount_total;
  return intOrZero(cents);
}

/**
 * isCancelled(order) -> boolean
 *
 * Every alias is checked independently rather than with `||`, for the reason spelled
 * out in addableOrder.js: an order record carries both `status` and `order_status`, and
 * short-circuiting on the first truthy one can miss the second. If ANY alias says
 * cancelled, it is cancelled.
 */
export function isCancelled(order) {
  if (!order || typeof order !== 'object') return false;
  if (order.is_cancelled === true || order.isCancelled === true) return true;
  return ['status', 'order_status', 'orderStatus'].some((k) => CANCELLED_STATES.includes(lower(order[k])));
}

/** True when the order has been paid or refunded, i.e. no longer an open tab. */
export function isSettled(order) {
  return ['payment_status', 'paymentStatus'].some((k) => SETTLED_PAYMENT_STATES.includes(lower(order?.[k])));
}

/**
 * salesChannelOf(order) -> 'STORE' | 'ONLINE' | 'MARKETPLACE'
 *
 * Total and disjoint (Property 3): every order gets exactly one label, and ONLINE is
 * the fallback so a shape nobody anticipated still contributes its revenue to the split
 * instead of vanishing. Silently dropping an order would make the channel split fail to
 * sum to the total, which is the one thing this classification must not do.
 *
 * STORE is exactly `orderSource === 'staff'` -- the same discriminator
 * `is_addable_order` uses, and the only value the Take Order path ever writes. Checked
 * FIRST and unconditionally: a staff order is a store sale regardless of how it is
 * paid or fulfilled.
 */
export function salesChannelOf(order) {
  if (!order || typeof order !== 'object') return ONLINE;
  if (lower(order.orderSource) === 'staff') return STORE;

  if (MARKETPLACE_ID_KEYS.some((k) => String(order[k] || '').trim() !== '')) return MARKETPLACE;
  if (String(order.marketplaceProvider || '').trim() !== '') return MARKETPLACE;

  const tokens = [order.order_source, order.delivery_channel, order.deliveryChannel].map(lower);
  if (tokens.some((t) => t && MARKETPLACE_TOKENS.includes(t))) return MARKETPLACE;

  return ONLINE;
}

/** True for a store order taken through Take Order. */
export function isStoreOrder(order) {
  return salesChannelOf(order) === STORE;
}

/** Dine-in vs to-go for a store order. Anything unrecognized reads as to-go. */
export function isDineIn(order) {
  return upper(order?.orderType) === 'DINE_IN';
}

/** `guestCount` as a positive integer, or null when absent (R4.2). */
export function coversOf(order) {
  const n = Number(order?.guestCount);
  if (!Number.isFinite(n)) return null;
  const covers = Math.floor(n);
  return covers > 0 ? covers : null;
}

/** Server display name, falling back to serverId then a placeholder (R5.8). */
export function serverLabel(order) {
  const name = String(order?.serverName || '').trim();
  if (name) return name;
  const id = String(order?.serverId || '').trim();
  return id || 'Unassigned';
}

function serverKeyOf(order) {
  const id = String(order?.serverId || '').trim();
  if (id) return id;
  const name = String(order?.serverName || '').trim();
  return name || UNASSIGNED_SERVER_KEY;
}

// ---------------------------------------------------------------------------
// Amendments
// ---------------------------------------------------------------------------

/**
 * Value added by ADD_ITEMS entries, in cents.
 *
 * Reads the amendment's OWN recorded `unit_price_cents`, never today's catalog price.
 * The backend deliberately preserves the price each line was quoted at, so re-pricing
 * from the catalog here would report a number that changes retroactively whenever the
 * menu is edited.
 */
function addedCentsOf(entry) {
  return arr(entry?.lines).reduce((acc, line) => {
    if (!line || typeof line !== 'object') return acc;
    return acc + intOrZero(line.Quantity ?? line.quantity) * intOrZero(line.unit_price_cents);
  }, 0);
}

/** Value removed by an ADJUST_ITEM / REMOVE_ITEM entry, in cents. */
function removedCentsOf(entry) {
  const line = entry?.line;
  if (!line || typeof line !== 'object') return 0;
  const before = intOrZero(line.beforeQuantity);
  const after = intOrZero(line.afterQuantity);
  const delta = before - after;
  if (delta <= 0) return 0;
  return delta * intOrZero(line.unit_price_cents);
}

/**
 * Per-order amendment summary. A malformed entry is skipped rather than allowed to
 * break the aggregate, so one bad record cannot empty a whole report section.
 *
 * `addCount`/`reduceCount` count ENTRIES; the rates built from this count ORDERS, so an
 * order edited five times is one upsell and one void, not five.
 */
export function amendmentSummary(order) {
  const out = { addCount: 0, reduceCount: 0, addedCents: 0, removedCents: 0, actors: [] };
  const seenActors = new Map();

  for (const entry of arr(order?.amendments)) {
    if (!entry || typeof entry !== 'object') continue;
    const type = upper(entry.type);
    const isAdd = type === ADD_TYPE;
    const isReduce = REDUCE_TYPES.includes(type);
    if (!isAdd && !isReduce) continue;

    let addedCents = 0;
    let removedCents = 0;
    if (isAdd) {
      out.addCount += 1;
      addedCents = addedCentsOf(entry);
      out.addedCents += addedCents;
    } else {
      out.reduceCount += 1;
      removedCents = removedCentsOf(entry);
      out.removedCents += removedCents;
    }

    const byId = String(entry.byId || '').trim();
    const byName = String(entry.byName || '').trim();
    const key = byId || byName || 'unknown';
    const actor = seenActors.get(key) || {
      key,
      byId,
      name: byName || byId || 'unknown',
      addCount: 0,
      reduceCount: 0,
      addedCents: 0,
      removedCents: 0,
    };
    actor.addCount += isAdd ? 1 : 0;
    actor.reduceCount += isReduce ? 1 : 0;
    actor.addedCents += addedCents;
    actor.removedCents += removedCents;
    seenActors.set(key, actor);
  }

  out.actors = Array.from(seenActors.values());
  return out;
}

// ---------------------------------------------------------------------------
// Time bucketing
// ---------------------------------------------------------------------------

function epochOf(order) {
  const n = Number(order?.order_timestamp ?? order?.orderTimestamp);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
}

/**
 * Local-time parts for an order: hour of day, weekday index, and a calendar day key.
 * `timeZone` is an IANA name; omitted means the caller's own timezone.
 */
function timeParts(epochSeconds, timeZone) {
  if (epochSeconds == null) return null;
  const dt = new Date(epochSeconds * 1000);
  if (Number.isNaN(dt.getTime())) return null;
  if (!timeZone) {
    return {
      hour: dt.getHours(),
      weekday: dt.getDay(),
      dayKey: `${dt.getFullYear()}-${dt.getMonth() + 1}-${dt.getDate()}`,
    };
  }
  try {
    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      hour12: false,
      weekday: 'short',
    });
    const parts = fmt.formatToParts(dt);
    const get = (t) => parts.find((p) => p.type === t)?.value || '';
    const hourRaw = Number(get('hour'));
    const dayKey = `${get('year')}-${get('month')}-${get('day')}`;
    const weekday = WEEKDAY_LABELS.indexOf(get('weekday'));
    return {
      hour: Number.isFinite(hourRaw) ? hourRaw % 24 : 0,
      weekday: weekday >= 0 ? weekday : dt.getDay(),
      dayKey,
    };
  } catch {
    return { hour: dt.getHours(), weekday: dt.getDay(), dayKey: `${dt.getFullYear()}-${dt.getMonth() + 1}-${dt.getDate()}` };
  }
}

/**
 * activeHours(orders, options) -> number
 *
 * Shift-length PROXY: per server per calendar day, the span from their first to their
 * last store order, floored at MIN_ACTIVE_HOURS and summed across days.
 *
 * THIS IS NOT MEASURED SHIFT LENGTH. The platform has no clock-in/clock-out, so this
 * under-counts setup/close time and any gap before the first order. It must be labelled
 * as derived wherever it is shown -- presenting it as measured time worked would be
 * misleading in a conversation about someone's pay.
 *
 * The floor exists so revenue-per-active-hour is never a division by zero for a server
 * with a single order (R5.7), which is otherwise the common case on a quiet day.
 */
export function activeHours(orders, options = {}) {
  const timeZone = options.timeZone;
  const byDay = new Map();
  for (const order of arr(orders)) {
    const at = epochOf(order);
    if (at == null) continue;
    const parts = timeParts(at, timeZone);
    if (!parts) continue;
    const bounds = byDay.get(parts.dayKey) || { first: at, last: at };
    bounds.first = Math.min(bounds.first, at);
    bounds.last = Math.max(bounds.last, at);
    byDay.set(parts.dayKey, bounds);
  }
  let total = 0;
  for (const { first, last } of byDay.values()) {
    total += Math.max(MIN_ACTIVE_HOURS, (last - first) / 3600);
  }
  return total;
}

// ---------------------------------------------------------------------------
// Channel split
// ---------------------------------------------------------------------------

/**
 * channelSplit(orders) -> per-channel revenue and counts, plus cancelled separately.
 *
 * `totalSalesCents` is the sum of the three channels by construction, so Property 4 is
 * an identity rather than something the caller has to trust. Cancelled orders are
 * counted in `cancelled` and in NO channel.
 */
export function channelSplit(orders) {
  const out = {
    [STORE]: { orders: 0, salesCents: 0 },
    [ONLINE]: { orders: 0, salesCents: 0 },
    [MARKETPLACE]: { orders: 0, salesCents: 0 },
    cancelled: { orders: 0, salesCents: 0 },
    totalOrders: 0,
    totalSalesCents: 0,
  };
  for (const order of arr(orders)) {
    const cents = orderRevenueCents(order);
    if (isCancelled(order)) {
      out.cancelled.orders += 1;
      out.cancelled.salesCents += cents;
      continue;
    }
    const channel = salesChannelOf(order);
    out[channel].orders += 1;
    out[channel].salesCents += cents;
    out.totalOrders += 1;
    out.totalSalesCents += cents;
  }
  return out;
}

/** The non-cancelled store orders in a set. The input to every store metric. */
export function storeOrders(orders) {
  return arr(orders).filter((o) => isStoreOrder(o) && !isCancelled(o));
}

// ---------------------------------------------------------------------------
// Settlement split (cash-payment-reporting): revenue grouped by HOW it was paid and by
// HOW it was fulfilled, for the end-of-day reconciliation summary. Cancelled orders are
// excluded from both, matching every other revenue figure this module produces.
// ---------------------------------------------------------------------------

/**
 * paymentTypeOf(order) -> 'CASH' | 'SQUARE' | 'STRIPE' | 'CLOVER' | 'PHONEPE' |
 * 'RAZORPAY' | 'UNPAID' | 'OTHER'
 *
 * MUST mirror the backend `derive_payment_type` (lambda/admin-orders-handler) so the
 * report and the raw data agree. Preference: an explicit `paymentType` from the backend
 * wins; otherwise derive the same way (cash marker -> provider -> Stripe-by-intent).
 * An unpaid order is 'UNPAID', never silently a tender.
 */
export function paymentTypeOf(order) {
  if (!order || typeof order !== 'object') return 'UNPAID';
  // Trust the backend-derived value when present.
  const explicit = upper(order.paymentType);
  if (explicit) return explicit;
  if (!isSettled(order)) return 'UNPAID';
  const refunded = ['payment_status', 'paymentStatus'].some((k) => lower(order[k]) === 'refunded');
  // A refund is still a tender that happened; classify by how it was taken, below.
  if (upper(order.settledTender) === 'CASH') return 'CASH';
  const provider = upper(order.posProvider);
  if (['SQUARE', 'STRIPE', 'CLOVER', 'PHONEPE', 'RAZORPAY'].includes(provider)) return provider;
  if (String(order.stripePaymentIntentId || '').trim()) return 'STRIPE';
  return refunded ? 'OTHER' : 'OTHER';
}

/** Human fulfillment bucket for the split. Coarse on purpose (reconciliation, not audit). */
export function fulfillmentBucketOf(order) {
  const raw = upper(
    order?.adminFulfillmentStatus || order?.displayStatus || order?.posFulfillmentState ||
    order?.squareFulfillmentState || order?.status || order?.order_status || ''
  ).replace(/\s+/g, '_');
  if (raw === 'PICKUP_COMPLETE') return 'Pickup complete';
  if (raw === 'DINE_IN_COMPLETE' || raw === 'DINEIN_COMPLETE') return 'Dine-in complete';
  if (raw === 'SHIPPED') return 'Shipped';
  if (raw === 'READY_FOR_PICKUP' || raw === 'READY') return 'Ready';
  if (raw === 'PENDING_PAYMENT' || raw === 'OPEN' || raw === '') return 'Open';
  if (raw === 'REFUNDED') return 'Refunded';
  if (raw === 'PROCESSING' || raw === 'PAID') return 'In progress';
  const words = raw.replace(/_/g, ' ').toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Open';
}

/**
 * settlementSplit(orders) -> { byPayment, byFulfillment, settledCents, settledOrders,
 * cancelled }
 *
 * `byPayment`/`byFulfillment` are arrays of { key, orders, salesCents } sorted by
 * salesCents desc. `settledCents` sums only PAID/REFUNDED orders (the money that
 * actually moved); unpaid orders appear in the payment split under 'UNPAID' but are NOT
 * added to settledCents, so the "collected" total is honest. Cancelled orders are
 * excluded from both splits and reported separately, matching channelSplit.
 */
export function settlementSplit(orders) {
  const payMap = new Map();
  const fulMap = new Map();
  let settledCents = 0;
  let settledOrders = 0;
  const cancelled = { orders: 0, salesCents: 0 };

  const bump = (map, key, cents) => {
    const row = map.get(key) || { key, orders: 0, salesCents: 0 };
    row.orders += 1;
    row.salesCents += cents;
    map.set(key, row);
  };

  for (const order of arr(orders)) {
    if (isCancelled(order)) {
      cancelled.orders += 1;
      cancelled.salesCents += orderRevenueCents(order);
      continue;
    }
    const cents = orderRevenueCents(order);
    const payKey = paymentTypeOf(order);
    bump(payMap, payKey, cents);
    bump(fulMap, fulfillmentBucketOf(order), cents);
    if (payKey !== 'UNPAID') {
      settledCents += cents;
      settledOrders += 1;
    }
  }

  const toSorted = (map) => Array.from(map.values()).sort((a, b) => b.salesCents - a.salesCents);
  return {
    byPayment: toSorted(payMap),
    byFulfillment: toSorted(fulMap),
    settledCents,
    settledOrders,
    cancelled,
  };
}

/** Display label for a payment-split key (mirrors the report formatPaymentType). */
export function paymentTypeLabel(key) {
  const t = upper(key);
  if (t === 'CASH') return 'Cash';
  if (t === 'SQUARE') return 'Card · Square';
  if (t === 'STRIPE') return 'Card · Stripe';
  if (t === 'CLOVER') return 'Card · Clover';
  if (t === 'PHONEPE') return 'PhonePe';
  if (t === 'RAZORPAY') return 'Razorpay';
  if (t === 'UNPAID') return 'Unpaid';
  return 'Other';
}

// ---------------------------------------------------------------------------
// Store metrics
// ---------------------------------------------------------------------------

function bucketTotals() {
  return { orders: 0, salesCents: 0, avgOrderValueCents: null };
}

function finishBucket(bucket) {
  bucket.avgOrderValueCents = bucket.orders ? Math.round(bucket.salesCents / bucket.orders) : null;
  return bucket;
}

/** Age buckets for unpaid store tickets (R4.8). Ascending, last one open-ended. */
const UNPAID_BUCKETS = [
  { label: 'Under 1h', maxHours: 1 },
  { label: '1–2h', maxHours: 2 },
  { label: '2–4h', maxHours: 4 },
  { label: 'Over 4h', maxHours: Infinity },
];

/**
 * storeMetrics(orders, options) -> every store-order figure on the page.
 *
 * `options.now` (epoch seconds) is injectable so unpaid-ticket ageing is testable;
 * defaults to the current clock.
 */
export function storeMetrics(orders, options = {}) {
  const timeZone = options.timeZone;
  const now = Number.isFinite(Number(options.now)) ? Math.floor(Number(options.now)) : Math.floor(Date.now() / 1000);

  const all = arr(orders).filter(isStoreOrder);
  const live = all.filter((o) => !isCancelled(o));
  const cancelled = all.filter(isCancelled);

  const dineIn = finishBucket(live.filter(isDineIn).reduce((acc, o) => {
    acc.orders += 1;
    acc.salesCents += orderRevenueCents(o);
    return acc;
  }, bucketTotals()));

  const toGo = finishBucket(live.filter((o) => !isDineIn(o)).reduce((acc, o) => {
    acc.orders += 1;
    acc.salesCents += orderRevenueCents(o);
    return acc;
  }, bucketTotals()));

  // Covers: only dine-in orders that actually recorded a guestCount participate, on
  // BOTH sides of the division. Dividing all dine-in revenue by the covers of only
  // some orders would inflate spend-per-cover, which is exactly the kind of quietly
  // wrong number that gets used to justify a decision (R4.2).
  let covers = 0;
  let coveredRevenueCents = 0;
  let ordersWithCovers = 0;
  let dineInWithoutCovers = 0;
  for (const order of live) {
    if (!isDineIn(order)) continue;
    const c = coversOf(order);
    if (c == null) {
      dineInWithoutCovers += 1;
      continue;
    }
    covers += c;
    coveredRevenueCents += orderRevenueCents(order);
    ordersWithCovers += 1;
  }
  const spendPerCoverRatio = ratio(coveredRevenueCents, covers);

  const byHour = Array.from({ length: 24 }, (_, hour) => ({ hour, label: `${String(hour).padStart(2, '0')}:00`, orders: 0, salesCents: 0 }));
  const byWeekday = WEEKDAY_LABELS.map((label, weekday) => ({ weekday, label, orders: 0, salesCents: 0 }));
  for (const order of live) {
    const parts = timeParts(epochOf(order), timeZone);
    if (!parts) continue;
    const cents = orderRevenueCents(order);
    if (byHour[parts.hour]) {
      byHour[parts.hour].orders += 1;
      byHour[parts.hour].salesCents += cents;
    }
    if (byWeekday[parts.weekday]) {
      byWeekday[parts.weekday].orders += 1;
      byWeekday[parts.weekday].salesCents += cents;
    }
  }

  // Per-table. To-go orders have no table and are excluded rather than grouped under
  // a blank label that would read as a real table.
  const tableMap = new Map();
  for (const order of live) {
    const label = String(order?.tableLabel || '').trim();
    if (!label) continue;
    const row = tableMap.get(label) || { tableLabel: label, turns: 0, salesCents: 0, avgOrderValueCents: null, parties: new Set() };
    row.turns += 1;
    row.salesCents += orderRevenueCents(order);
    const party = String(order?.partyLabel || '').trim();
    if (party) row.parties.add(party);
    tableMap.set(label, row);
  }
  const tables = Array.from(tableMap.values())
    .map((row) => ({
      tableLabel: row.tableLabel,
      turns: row.turns,
      orders: row.turns,
      salesCents: row.salesCents,
      avgOrderValueCents: row.turns ? Math.round(row.salesCents / row.turns) : null,
      partyCount: row.parties.size,
    }))
    .sort((a, b) => b.salesCents - a.salesCents);

  let upsoldOrders = 0;
  let voidedOrders = 0;
  let addedCents = 0;
  let removedCents = 0;
  for (const order of live) {
    const summary = amendmentSummary(order);
    if (summary.addCount > 0) upsoldOrders += 1;
    if (summary.reduceCount > 0) voidedOrders += 1;
    addedCents += summary.addedCents;
    removedCents += summary.removedCents;
  }

  const unpaidList = live
    .filter((o) => !isSettled(o))
    .map((o) => {
      const at = epochOf(o);
      const ageHours = at == null ? null : Math.max(0, (now - at) / 3600);
      return {
        orderNumber: String(o?.order_number || ''),
        tableLabel: String(o?.tableLabel || '').trim(),
        serverName: serverLabel(o),
        salesCents: orderRevenueCents(o),
        openedAt: at,
        ageHours,
      };
    })
    .sort((a, b) => (b.ageHours ?? -1) - (a.ageHours ?? -1));

  const unpaidBuckets = UNPAID_BUCKETS.map((b) => ({ label: b.label, orders: 0, salesCents: 0 }));
  for (const ticket of unpaidList) {
    const age = ticket.ageHours == null ? 0 : ticket.ageHours;
    const idx = UNPAID_BUCKETS.findIndex((b) => age < b.maxHours);
    const target = unpaidBuckets[idx >= 0 ? idx : unpaidBuckets.length - 1];
    target.orders += 1;
    target.salesCents += ticket.salesCents;
  }

  return {
    orders: live.length,
    salesCents: live.reduce((acc, o) => acc + orderRevenueCents(o), 0),
    avgOrderValueCents: live.length ? Math.round(live.reduce((acc, o) => acc + orderRevenueCents(o), 0) / live.length) : null,
    dineIn,
    toGo,
    covers,
    ordersWithCovers,
    dineInWithoutCovers,
    spendPerCoverCents: spendPerCoverRatio == null ? null : Math.round(spendPerCoverRatio),
    byHour,
    byWeekday,
    tables,
    upsellRate: ratio(upsoldOrders, live.length),
    upsoldOrders,
    valueAddedCents: addedCents,
    voidRate: ratio(voidedOrders, live.length),
    voidedOrders,
    valueRemovedCents: removedCents,
    unpaid: {
      orders: unpaidList.length,
      salesCents: unpaidList.reduce((acc, t) => acc + t.salesCents, 0),
      buckets: unpaidBuckets,
      tickets: unpaidList,
      oldestAgeHours: unpaidList.length ? unpaidList[0].ageHours : null,
    },
    cancelled: {
      orders: cancelled.length,
      salesCents: cancelled.reduce((acc, o) => acc + orderRevenueCents(o), 0),
    },
  };
}

// ---------------------------------------------------------------------------
// Per-server metrics
// ---------------------------------------------------------------------------

/**
 * serverMetrics(orders, options) -> { servers, additions }
 *
 * OPENER-TAKES-ALL (D1). A store order's revenue is credited entirely to the server on
 * the order -- whoever opened the ticket -- even when someone else later added to it.
 * Proportional splitting is arguably fairer but produces numbers nobody can reconcile
 * by hand, which is disqualifying for something attached to staff money.
 *
 * Orders with NO serverId are grouped under UNASSIGNED_SERVER_KEY rather than dropped.
 * Dropping them would silently break the conservation identity (Property 5): per-server
 * revenue would no longer sum to store revenue, and the gap would look like a rounding
 * artifact instead of missing orders.
 *
 * `additions` is the counterweight to opener-takes-all (R5.5): it reports what each
 * user did to orders they did not open, so their contribution is visible even though
 * none of that revenue is credited to them.
 */
export function serverMetrics(orders, options = {}) {
  const live = storeOrders(orders);

  const byServer = new Map();
  const byActor = new Map();

  const serverRow = (key, label) => {
    const existing = byServer.get(key);
    if (existing) {
      if (label && label !== 'Unassigned' && existing.serverName === 'Unassigned') existing.serverName = label;
      return existing;
    }
    const row = {
      serverKey: key,
      serverId: key,
      serverName: label,
      orders: 0,
      salesCents: 0,
      avgOrderValueCents: null,
      covers: 0,
      coveredRevenueCents: 0,
      spendPerCoverCents: null,
      upsoldOrders: 0,
      upsellRate: null,
      valueAddedCents: 0,
      voidedOrders: 0,
      voidRate: null,
      valueRemovedCents: 0,
      activeHours: 0,
      revenuePerActiveHourCents: null,
      _orders: [],
      _items: new Map(),
    };
    byServer.set(key, row);
    return row;
  };

  const actorRow = (key, name) => {
    const existing = byActor.get(key);
    if (existing) {
      if (name && name !== 'unknown' && existing.name === 'unknown') existing.name = name;
      return existing;
    }
    const row = {
      actorKey: key,
      byId: key,
      name: name || 'unknown',
      ownOrdersTouched: 0,
      othersOrdersTouched: 0,
      addCount: 0,
      reduceCount: 0,
      valueAddedCents: 0,
      valueRemovedCents: 0,
    };
    byActor.set(key, row);
    return row;
  };

  for (const order of live) {
    const key = serverKeyOf(order);
    const row = serverRow(key, serverLabel(order));
    const cents = orderRevenueCents(order);

    row.orders += 1;
    row.salesCents += cents;
    row._orders.push(order);

    if (isDineIn(order)) {
      const c = coversOf(order);
      if (c != null) {
        row.covers += c;
        row.coveredRevenueCents += cents;
      }
    }

    const summary = amendmentSummary(order);
    if (summary.addCount > 0) row.upsoldOrders += 1;
    if (summary.reduceCount > 0) row.voidedOrders += 1;
    row.valueAddedCents += summary.addedCents;
    row.valueRemovedCents += summary.removedCents;

    for (const item of arr(order.items)) {
      const pid = String(item?.ProductID || item?.productId || '').trim();
      const itemKey = pid || String(item?.name || item?.ProductName || '').trim();
      if (!itemKey) continue;
      const qty = intOrZero(item?.quantity ?? item?.Quantity);
      const unit = intOrZero(item?.unit_price_cents ?? item?.unitPriceCents ?? item?.price_cents ?? item?.priceCents);
      const entry = row._items.get(itemKey) || { key: itemKey, productId: pid, name: String(item?.name || item?.ProductName || itemKey).trim(), quantity: 0, revenueCents: 0 };
      entry.quantity += qty;
      entry.revenueCents += qty * unit;
      row._items.set(itemKey, entry);
    }

    // Amendment actors, split by whether they opened this ticket.
    for (const actor of summary.actors) {
      const a = actorRow(actor.key, actor.name);
      const isOpener = actor.byId ? actor.byId === String(order.serverId || '').trim() : actor.name === serverLabel(order);
      if (isOpener) a.ownOrdersTouched += 1;
      else a.othersOrdersTouched += 1;
      a.addCount += actor.addCount;
      a.reduceCount += actor.reduceCount;
      a.valueAddedCents += actor.addedCents;
      a.valueRemovedCents += actor.removedCents;
    }
  }

  const servers = Array.from(byServer.values()).map((row) => {
    const hours = activeHours(row._orders, options);
    const spend = ratio(row.coveredRevenueCents, row.covers);
    const perHour = ratio(row.salesCents, hours);
    const topItems = Array.from(row._items.values()).sort((a, b) => b.revenueCents - a.revenueCents).slice(0, 5);
    return {
      serverKey: row.serverKey,
      serverId: row.serverId,
      serverName: row.serverName,
      orders: row.orders,
      salesCents: row.salesCents,
      avgOrderValueCents: row.orders ? Math.round(row.salesCents / row.orders) : null,
      covers: row.covers,
      spendPerCoverCents: spend == null ? null : Math.round(spend),
      upsoldOrders: row.upsoldOrders,
      upsellRate: ratio(row.upsoldOrders, row.orders),
      valueAddedCents: row.valueAddedCents,
      voidedOrders: row.voidedOrders,
      voidRate: ratio(row.voidedOrders, row.orders),
      valueRemovedCents: row.valueRemovedCents,
      activeHours: hours,
      revenuePerActiveHourCents: perHour == null ? null : Math.round(perHour),
      topItems,
    };
  });

  servers.sort((a, b) => b.salesCents - a.salesCents);

  const additions = Array.from(byActor.values()).sort((a, b) => b.valueAddedCents - a.valueAddedCents);

  return { servers, additions };
}

/**
 * Sort a leaderboard by any column (R5.3). `null` always sorts last regardless of
 * direction, because an unavailable metric is not a low score -- ranking a server with
 * no covers below one with a poor spend-per-cover would misrepresent them.
 */
export function sortServers(servers, column, direction = 'desc') {
  const dir = direction === 'asc' ? 1 : -1;
  return arr(servers).slice().sort((a, b) => {
    const av = a?.[column];
    const bv = b?.[column];
    const aNull = av == null;
    const bNull = bv == null;
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;
    if (typeof av === 'string' || typeof bv === 'string') {
      return String(av).localeCompare(String(bv)) * dir;
    }
    return (Number(av) - Number(bv)) * dir;
  });
}

/**
 * Top-selling items restricted to store orders (R4.5).
 *
 * `productById` is optional and only supplies display names/categories, mirroring the
 * page's existing topItems memo. Revenue uses the price recorded ON the order line, not
 * the catalog, so a later menu price change does not retroactively rewrite history.
 */
export function storeTopItems(orders, options = {}) {
  const productById = options.productById || {};
  const limit = Number.isFinite(Number(options.limit)) ? Math.max(1, Math.floor(Number(options.limit))) : 12;
  const map = new Map();

  for (const order of storeOrders(orders)) {
    for (const item of arr(order.items)) {
      const pid = String(item?.ProductID || item?.productId || '').trim();
      const key = pid || String(item?.name || item?.ProductName || '').trim();
      if (!key) continue;
      const qty = intOrZero(item?.quantity ?? item?.Quantity);
      const base = intOrZero(item?.unit_price_cents ?? item?.unitPriceCents ?? item?.price_cents ?? item?.priceCents);
      const mod = intOrZero(item?.modifier_price_cents ?? item?.modifierPriceCents);
      const unit = base + mod;
      const prod = pid ? productById[pid] : null;
      const entry = map.get(key) || { key, productId: pid, name: '', quantity: 0, revenueCents: 0 };
      entry.quantity += qty;
      entry.revenueCents += qty * unit;
      const displayName = String(prod?.ProductName || prod?.name || item?.name || item?.ProductName || key).trim();
      if (displayName) entry.name = displayName;
      map.set(key, entry);
    }
  }

  return Array.from(map.values()).sort((a, b) => b.revenueCents - a.revenueCents).slice(0, limit);
}
